/**
 * Route → page guide. A route that is not listed here simply has no "?" button, which is what
 * lets the manual roll out page by page instead of all at once.
 *
 * Keys mirror the `<Route path>` patterns in `src/App.tsx`, `:param` segments included — a detail
 * page is a different page with a different guide, not a longer version of its parent.
 *
 * Keep this file free of React/UI imports and of baseUrl-style imports (`"pages/…"`): the Node
 * scripts (`scripts/build-user-manual.mjs`) import it directly, and either would break them.
 */
import { adminPageHelp } from "../pages/AdminPage/help";
import { companyBenefitImplementationPageHelp } from "../pages/CompanyBenefitImplementationPage/help";
import { companyPackagesPageHelp } from "../pages/CompanyPackagesPage/help";
import { companyProfilePageHelp } from "../pages/CompanyProfilePage/help";
import { countryPageHelp } from "../pages/CountryPage/help";
import { hrNeedsAttentionPageHelp } from "../pages/HrNeedsAttentionPage/help";
import { hrPackageEmployeesPageHelp } from "../pages/HrPackageEmployeesPage/help";
import { hrPackagesPageHelp } from "../pages/HrPackagesPage/help";
import { hrPageHelp } from "../pages/HrPage/help";
import type { PageHelp } from "./types";

export const HELP_REGISTRY: Record<string, PageHelp> = {
  "/admin": adminPageHelp,
  "/country": countryPageHelp,
  "/company-profile": companyProfilePageHelp,
  "/company-profile/:categoryId/:countryCode/:clientInternalNumberGOS":
    companyBenefitImplementationPageHelp,
  "/company-profile/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS":
    companyPackagesPageHelp,
  "/hr": hrPageHelp,
  "/hr/needs-attention": hrNeedsAttentionPageHelp,
  "/hr/packages/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS":
    hrPackagesPageHelp,
  "/hr/employees/:packageId/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS":
    hrPackageEmployeesPageHelp,
};

const isPattern = (route: string): boolean => route.includes("/:");

/** Segment-wise match, where `:param` accepts any single non-empty segment. */
export const matchesRoute = (route: string, pathname: string): boolean => {
  const pattern = route.split("/");
  const actual = pathname.split("/");
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, i) =>
    segment.startsWith(":") ? (actual[i] ?? "").length > 0 : segment === actual[i]
  );
};

export const helpForPath = (rawPathname: string): PageHelp | null => {
  // A trailing slash is the same page.
  const pathname =
    rawPathname.length > 1 && rawPathname.endsWith("/") ? rawPathname.slice(0, -1) : rawPathname;

  const exact = HELP_REGISTRY[pathname];
  if (exact) return exact;

  // Patterns before prefixes: /hr/packages/… belongs to the packages guide, not to the /hr guide
  // it happens to sit under. Fewest params wins.
  const pattern = Object.keys(HELP_REGISTRY)
    .filter((route) => isPattern(route) && matchesRoute(route, pathname))
    .sort((a, b) => a.split("/:").length - b.split("/:").length)[0];
  if (pattern) return HELP_REGISTRY[pattern] ?? null;

  // Prefix match so a deeper route with no guide of its own still gets its parent's guide.
  // Longest match wins, so /a/b never shadows /a/b/c.
  const hit = Object.keys(HELP_REGISTRY)
    .filter((route) => !isPattern(route) && pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? HELP_REGISTRY[hit] ?? null : null;
};
